import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import SurveyService from "../services/survey.service";
import { buildValidationErrorMessage, looksLikePoBox, PO_BOX_ERROR_MESSAGE } from "../util/surveyValidation";
import { resolveImageUrl, resolveSwatchUrl } from '../utils/imageUrl';

const apiUrl = process.env.REACT_APP_LOCALHOST_URL;

const US_STATES = [
    ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
    ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
    ['DC', 'District Of Columbia'], ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'],
    ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'],
    ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'],
    ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'],
    ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'],
    ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'],
    ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'],
    ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'],
    ['SC', 'South Carolina'], ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'],
    ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'],
    ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming']
];

const ZIP_CODE_REGEX = /^[0-9]{5}$/;

const getImageUrl = (imageUrl) => resolveImageUrl(imageUrl);

/** Prefer featured color when set; otherwise first catalog color. */
const resolveFeaturedItem = (product, featuredColorId) => {
    if (!product?.items?.length) return null;
    if (featuredColorId != null) {
        const match = product.items.find(i =>
            i.colorId === featuredColorId || i.colorId === Number(featuredColorId)
        );
        if (match) return match;
    }
    return product.items[0];
};

// Project a raw user input onto a mask pattern.
// Mask syntax: 9 = digit, A = letter, * = digit or letter; any other character
// is a literal that is auto-inserted as the respondent types.
const applyMask = (value, mask) => {
    if (!mask) return value;
    const v = String(value || '');
    let out = '';
    let mi = 0;
    let vi = 0;
    while (mi < mask.length && vi < v.length) {
        const m = mask[mi];
        const c = v[vi];
        if (m === '9') {
            if (c >= '0' && c <= '9') { out += c; mi++; vi++; } else { vi++; }
        } else if (m === 'A') {
            if ((c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z')) { out += c; mi++; vi++; } else { vi++; }
        } else if (m === '*') {
            if ((c >= '0' && c <= '9') || (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z')) { out += c; mi++; vi++; } else { vi++; }
        } else {
            // Literal mask character — auto-insert, but not as a trailing character
            // when the user has stopped typing (so backspace doesn't get blocked).
            if (vi >= v.length) break;
            out += m;
            mi++;
            if (c === m) vi++;
        }
    }
    return out;
};

const PublicSurvey = () => {
    const { code } = useParams();
    const [survey, setSurvey] = useState(null);
    const [responses, setResponses] = useState({});
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Product selection state - keyed by field id
    const [productData, setProductData] = useState({});
    const [productSelections, setProductSelections] = useState({});

    // Address selection state - keyed by field id
    const [addressData, setAddressData] = useState([]); // Available addresses from subsite
    const [addressSelections, setAddressSelections] = useState({}); // Manual address entries or selected address id

    useEffect(() => {
        const fetchSurvey = async () => {
            try {
                const response = await SurveyService.getPublicSurvey(code);
                // Backend returns { closed: true, title, description } when the
                // survey exists but is administratively inactive — render the
                // closed page with the survey name instead of the generic message.
                if (response.data && response.data.closed) {
                    setSurvey(response.data);
                    return;
                }
                setSurvey(response.data);
                // Initialize responses
                const initialResponses = {};
                const initialProductSelections = {};
                const initialAddressSelections = {};
                response.data.survey_fields?.forEach(field => {
                    initialResponses[field.id] = '';
                    if (field.fieldType === 'product') {
                        const productConfig = field.survey_survey_fields || {};
                        const alts = productConfig.alternateProducts
                            ? (typeof productConfig.alternateProducts === 'string' ? JSON.parse(productConfig.alternateProducts) : productConfig.alternateProducts)
                            : (productConfig.styleId ? [{ styleId: productConfig.styleId, label: productConfig.styleIdLabel || 'Option 1' }, ...(productConfig.styleId2 ? [{ styleId: productConfig.styleId2, label: productConfig.styleId2Label || 'Option 2' }] : [])] : []);
                        // Auto-select index 0 if there's only one option; otherwise require user to pick
                        initialProductSelections[field.id] = {
                            colorId: null,
                            sizeId: null,
                            sizeName: '',
                            colorName: '',
                            productId: null,
                            selectedOption: alts.length <= 1 ? 0 : null
                        };
                    }
                    if (field.fieldType === 'address') {
                        if (field.addressType === 'manual') {
                            initialAddressSelections[field.id] = { street: '', street2: '', city: '', state: '', zipCode: '' };
                        } else {
                            initialAddressSelections[field.id] = { addressId: null };
                        }
                    }
                });
                setResponses(initialResponses);
                setProductSelections(initialProductSelections);
                setAddressSelections(initialAddressSelections);

                const subsiteId = response.data.subsiteId;

                // Fetch product data for product fields — supports N alternates
                const productFields = response.data.survey_fields?.filter(f => f.fieldType === 'product');
                if (productFields?.length > 0) {
                    const productDataMap = {};
                    for (const field of productFields) {
                        const productConfig = field.survey_survey_fields || {};
                        // Resolve the alternates list (new format or legacy styleId/styleId2)
                        let alts = [];
                        if (productConfig.alternateProducts) {
                            const parsed = typeof productConfig.alternateProducts === 'string'
                                ? JSON.parse(productConfig.alternateProducts)
                                : productConfig.alternateProducts;
                            alts = parsed || [];
                        } else if (productConfig.styleId) {
                            alts = [{ styleId: productConfig.styleId, label: productConfig.styleIdLabel || 'Option 1' }];
                            if (productConfig.styleId2) {
                                alts.push({ styleId: productConfig.styleId2, label: productConfig.styleId2Label || 'Option 2' });
                            }
                        }
                        if (alts.length === 0) continue;
                        try {
                            const loaded = [];
                            for (const alt of alts) {
                                const productResponse = await SurveyService.getProductForSurvey(alt.styleId, subsiteId);
                                loaded.push({
                                    product: productResponse.data,
                                    label: alt.label,
                                    styleId: alt.styleId,
                                    featuredColorId: alt.featuredColorId ?? null
                                });
                            }
                            productDataMap[field.id] = { alternates: loaded };
                        } catch (err) {
                            console.error(`Error fetching product for field ${field.id}:`, err);
                        }
                    }
                    setProductData(productDataMap);
                    // Apply featured colors for fields that already have an option selected (single-option auto-select)
                    setProductSelections(prev => {
                        const next = { ...prev };
                        Object.keys(productDataMap).forEach(fieldId => {
                            const sel = next[fieldId];
                            if (!sel || sel.selectedOption == null) return;
                            const alt = productDataMap[fieldId].alternates[sel.selectedOption];
                            if (!alt) return;
                            const item = resolveFeaturedItem(alt.product, alt.featuredColorId);
                            if (item) {
                                next[fieldId] = {
                                    ...sel,
                                    colorId: item.colorId,
                                    colorName: item.colorName
                                };
                            }
                        });
                        return next;
                    });
                }

                // Fetch addresses for address select fields
                const addressSelectFields = response.data.survey_fields?.filter(f => f.fieldType === 'address' && f.addressType === 'select');
                if (addressSelectFields?.length > 0 && subsiteId) {
                    try {
                        const addressResponse = await SurveyService.getAddressesForSurvey(subsiteId);
                        setAddressData(addressResponse.data || []);
                    } catch (err) {
                        console.error("Error fetching addresses:", err);
                    }
                }
            } catch (err) {
                setError("Survey not found or is no longer active.");
            } finally {
                setLoading(false);
            }
        };
        fetchSurvey();
    }, [code]);

    const validateField = (field, value) => {
        // Image, gallery, and statictext fields are display-only, no validation needed
        if (field.fieldType === 'image' || field.fieldType === 'gallery' || field.fieldType === 'statictext') {
            return null;
        }
        if (field.fieldType === 'product') {
            const selection = productSelections[field.id];
            const productInfo = productData[field.id];
            const hasMultipleOptions = (productInfo?.alternates?.length || 0) > 1;
            if (field.required && hasMultipleOptions && selection?.selectedOption == null) {
                return `Please select an option for ${field.title}`;
            }
            if (field.required && (!selection?.colorId || !selection?.sizeId)) {
                return `Please select a color and size for ${field.title}`;
            }
            return null;
        }
        if (field.fieldType === 'address') {
            const selection = addressSelections[field.id];
            if (field.required) {
                if (field.addressType === 'manual') {
                    if (!selection?.street || !selection?.city || !selection?.state || !selection?.zipCode) {
                        return `Please fill in all required address fields for ${field.title}`;
                    }
                } else {
                    if (!selection?.addressId) {
                        return `Please select an address for ${field.title}`;
                    }
                }
            }
            if (field.addressType === 'manual' && selection?.zipCode && !ZIP_CODE_REGEX.test(selection.zipCode)) {
                return `Zip code needs 5 digits`;
            }
            if (field.rejectPoBox) {
                let street = selection?.street;
                let street2 = selection?.street2;
                if (field.addressType === 'select' && selection?.addressId) {
                    const addr = addressData.find(a => a.id === selection.addressId);
                    street = addr?.street;
                    street2 = addr?.street2;
                }
                if (looksLikePoBox(street, street2)) {
                    return PO_BOX_ERROR_MESSAGE;
                }
            }
            return null;
        }
        if (field.required && !value) {
            return `${field.title} is required`;
        }
        if (field.validation && value) {
            const regex = new RegExp(field.validation);
            if (!regex.test(value)) {
                return buildValidationErrorMessage(field);
            }
        }
        if (field.fieldType === 'number' && value && isNaN(value)) {
            return `${field.title} must be a number`;
        }
        return null;
    };

    const handleChange = (fieldId, value) => {
        setResponses(prev => ({ ...prev, [fieldId]: value }));
        // Clear error for this field
        setErrors(prev => ({ ...prev, [fieldId]: null }));
    };

    const handleAddressChange = (fieldId, addressField, value) => {
        setAddressSelections(prev => ({
            ...prev,
            [fieldId]: {
                ...prev[fieldId],
                [addressField]: value
            }
        }));
        setErrors(prev => ({ ...prev, [fieldId]: null }));
    };

    const handleAddressSelect = (fieldId, addressId) => {
        setAddressSelections(prev => ({
            ...prev,
            [fieldId]: { addressId: addressId ? parseInt(addressId) : null }
        }));
        setErrors(prev => ({ ...prev, [fieldId]: null }));
    };

    const handleProductOptionSelect = (fieldId, optionIndex) => {
        const alt = productData[fieldId]?.alternates?.[optionIndex];
        const featured = resolveFeaturedItem(alt?.product, alt?.featuredColorId);
        setProductSelections(prev => ({
            ...prev,
            [fieldId]: {
                colorId: featured?.colorId || null,
                sizeId: null,
                sizeName: '',
                colorName: featured?.colorName || '',
                productId: null,
                selectedOption: optionIndex
            }
        }));
        setErrors(prev => ({ ...prev, [fieldId]: null }));
    };

    const handleProductColorSelect = (fieldId, item) => {
        setProductSelections(prev => ({
            ...prev,
            [fieldId]: {
                ...prev[fieldId],
                colorId: item.colorId,
                colorName: item.colorName,
                sizeId: null,
                sizeName: '',
                productId: null
            }
        }));
        setErrors(prev => ({ ...prev, [fieldId]: null }));
    };

    const handleProductSizeSelect = (fieldId, size) => {
        const selection = productSelections[fieldId];
        const productInfo = productData[fieldId];
        // Get the selected product based on option index
        const idx = selection?.selectedOption ?? 0;
        const selectedAlt = productInfo?.alternates?.[idx];
        const selectedProduct = selectedAlt?.product;

        setProductSelections(prev => ({
            ...prev,
            [fieldId]: {
                ...prev[fieldId],
                sizeId: size.sizeId,
                sizeName: size.sizeName,
                productId: size.productId
            }
        }));

        // Update the response with JSON of the selection
        const responseValue = JSON.stringify({
            selectedOption: idx,
            optionLabel: selectedAlt?.label || '',
            styleId: selectedProduct?.styleId,
            styleTitle: selectedProduct?.title,
            colorId: selection?.colorId,
            colorName: selection?.colorName,
            sizeId: size.sizeId,
            sizeName: size.sizeName,
            productId: size.productId
        });
        setResponses(prev => ({ ...prev, [fieldId]: responseValue }));
        setErrors(prev => ({ ...prev, [fieldId]: null }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate all fields
        const newErrors = {};
        survey.survey_fields?.forEach(field => {
            const error = validateField(field, responses[field.id]);
            if (error) newErrors[field.id] = error;
        });

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Build final responses including address data
        const finalResponses = { ...responses };
        survey.survey_fields?.forEach(field => {
            // Display-only fields don't contribute responses
            if (field.fieldType === 'image' || field.fieldType === 'gallery' || field.fieldType === 'statictext') {
                delete finalResponses[field.id];
                return;
            }
            if (field.fieldType === 'address') {
                const selection = addressSelections[field.id];
                if (field.addressType === 'select' && selection?.addressId) {
                    const addr = addressData.find(a => a.id === selection.addressId);
                    finalResponses[field.id] = JSON.stringify({
                        type: 'select',
                        addressId: selection.addressId,
                        name: addr?.name,
                        street: addr?.street,
                        street2: addr?.street2,
                        city: addr?.city,
                        state: addr?.state,
                        zipCode: addr?.zipCode
                    });
                } else if (field.addressType === 'manual') {
                    finalResponses[field.id] = JSON.stringify({
                        type: 'manual',
                        street: selection?.street,
                        street2: selection?.street2,
                        city: selection?.city,
                        state: selection?.state,
                        zipCode: selection?.zipCode
                    });
                }
            }
        });

        try {
            await SurveyService.submitSurvey(code, finalResponses);
            setSubmitted(true);
        } catch (err) {
            const serverMessage = err?.response?.data?.message;
            setError(serverMessage || "Failed to submit survey. Please try again.");
        }
    };

    // Left-column product field: option selector + color swatches + size picker (no images)
    const renderProductField = (field) => {
        const productInfo = productData[field.id];
        const selection = productSelections[field.id] || {};
        const fieldError = errors[field.id];

        if (!productInfo) return <p className="text-muted">Loading product...</p>;

        const alternates = productInfo.alternates || [];
        const hasMultipleOptions = alternates.length > 1;
        const activeIdx = selection.selectedOption ?? 0;
        const activeAlt = alternates[activeIdx];
        const activeProduct = activeAlt?.product;
        const colorItem = activeProduct?.items?.find(i => i.colorId === selection.colorId)
            || resolveFeaturedItem(activeProduct, activeAlt?.featuredColorId);
        const productSizes = colorItem?.sizes || [];
        const productLogos = colorItem?.logos || [];

        return (
            <div style={{
                border: fieldError ? "2px solid #dc3545" : "1px solid #dee2e6",
                borderRadius: 8,
                padding: 16,
                backgroundColor: '#f8f9fa'
            }}>
                {/* Option selector — dropdown select */}
                {hasMultipleOptions && (
                    <Form.Group className="mb-3">
                        <Form.Select
                            value={selection.selectedOption ?? ''}
                            onChange={(e) => handleProductOptionSelect(field.id, parseInt(e.target.value))}
                        >
                            <option value="" disabled>— Select a Product —</option>
                            {alternates.map((alt, idx) => (
                                <option key={idx} value={idx}>
                                    {alt.label || `Option ${idx + 1}`}{alt.product?.title ? ` — ${alt.product.title}` : ''}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                )}

                {(!hasMultipleOptions || selection.selectedOption != null) && activeProduct ? (
                    <>
                        {/* Product thumbnail with logo overlays (catalog-style); shows default color until user picks one */}
                        {colorItem?.frontModelImage && (
                            <div className="mb-3" style={{ display: 'inline-block', position: 'relative', width: 250 }}>
                                <img
                                    src={getImageUrl(colorItem.frontModelImage)}
                                    alt={colorItem.colorName}
                                    style={{ width: '100%', borderRadius: 4, display: 'block' }}
                                />
                                {productLogos.map((logo, i) => (
                                    <img
                                        key={i}
                                        src={apiUrl + logo.decoration_logo.imageUrl}
                                        alt={logo.decoration_logo.decorationName}
                                        style={{
                                            position: 'absolute',
                                            left: logo.x + '%',
                                            top: logo.y + '%',
                                            width: logo.width + '%',
                                            transform: 'rotate(' + logo.rotate + 'deg)'
                                        }}
                                    />
                                ))}
                            </div>
                        )}
                        {/* Product title */}
                        {activeProduct.title && (
                            <div style={{ fontWeight: 600, fontSize: '0.9em', marginBottom: 6 }}>
                                {activeProduct.title}
                            </div>
                        )}

                        {/* Color Swatches */}
                        <div className="mb-2">
                            {activeProduct.items?.map(item => (
                                <div
                                    key={item.colorId}
                                    onClick={() => handleProductColorSelect(field.id, item)}
                                    style={{
                                        marginRight: 6, display: "inline-block", cursor: 'pointer',
                                        border: selection.colorId === item.colorId ? '3px solid #0d6efd' : '3px solid transparent',
                                        borderRadius: 2
                                    }}
                                >
                                    {item.hexColor && item.hexColor !== "" && !(item.squareImageName && item.squareImageName !== "") && (
                                        <div style={{ backgroundColor: item.hexColor, width: 32, height: 32 }} />
                                    )}
                                    {item.squareImageName && item.squareImageName !== "" && (
                                        <img src={resolveSwatchUrl(item.squareImageName)} height={32} alt={item.colorName} />
                                    )}
                                </div>
                            ))}
                        </div>


                        {colorItem && (
                            <div style={{ fontSize: '0.85em', color: '#555', marginBottom: 8 }}>
                                Color: <strong>{colorItem.colorName}</strong>
                            </div>
                        )}


                        {/* Decorations list (only when no thumbnail, i.e. no frontModelImage) */}
                        {productLogos.length > 0 && !colorItem?.frontModelImage && (
                            <div className="mb-2">
                                <small><strong>Decorations:</strong></small>
                                {productLogos.map((logo, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                                        <img src={apiUrl + logo.decoration_logo.imageUrl} alt={logo.decoration_logo.decorationName}
                                            style={{ width: 28, height: 28, objectFit: 'contain', backgroundColor: '#f5f5f5', borderRadius: 3, padding: 2 }} />
                                        <small>{logo.decoration_logo.decorationName}</small>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Size Selection */}
                        <Form.Group className="mb-2">
                            <Form.Label><strong>Select Size:</strong></Form.Label>
                            <Form.Select
                                value={selection.sizeId || ''}
                                onChange={(e) => {
                                    const size = productSizes.find(s => s.sizeId === parseInt(e.target.value));
                                    if (size) handleProductSizeSelect(field.id, size);
                                }}
                                isInvalid={!!fieldError}
                            >
                                <option value="">Select a Size</option>
                                {productSizes.sort((a, b) => a.order - b.order).map(size => (
                                    <option key={size.sizeId} value={size.sizeId}>{size.sizeName}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        {selection.colorId && selection.sizeId && (
                            <Alert variant="success" className="mb-0 py-1">
                                <strong>Selected:</strong> {selection.colorName} — {selection.sizeName}
                            </Alert>
                        )}

                        {/* Spec Sheet / Measurements links */}
                        {(() => {
                            const firstItem = activeProduct.items?.[0];
                            const specSheet = firstItem?.specSheet;
                            const measurements = firstItem?.measurements;
                            if (!specSheet && !measurements) return null;
                            return (
                                <div className="mt-2" style={{ fontSize: '0.85em' }}>
                                    {specSheet && (
                                        <a
                                            href={specSheet.startsWith('http') ? specSheet : `https://www.apparelvideos.com/images/specsheet/pdf/specsheet/${specSheet}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{ marginRight: 12 }}
                                        >
                                            Spec Sheet
                                        </a>
                                    )}
                                    {measurements && (
                                        <a
                                            href={measurements.startsWith('http') ? measurements : `https://www.apparelvideos.com/images/specsheet/pdf/productmeasurements/${measurements}`}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Measurements
                                        </a>
                                    )}
                                </div>
                            );
                        })()}
                    </>
                ) : hasMultipleOptions ? (
                    <p className="text-muted">Select an option above to continue.</p>
                ) : null}
            </div>
        );
    };

    // Product image panel: one image per alternate product option.
    // Active alternate uses selected (or featured) color; others use their featured color.
    const renderProductImagePanel = (field) => {
        const productInfo = productData[field.id];
        const selection = productSelections[field.id] || {};
        if (!productInfo) return null;

        const alternates = productInfo.alternates || [];
        if (alternates.length === 0) return null;

        const activeIdx = selection.selectedOption; // null means nothing chosen yet

        return (
            <div className="mb-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                {alternates.map((alt, idx) => {
                    const product = alt.product;
                    if (!product) return null;

                    const isActive = activeIdx != null && idx === activeIdx;
                    const colorItem = isActive
                        ? (product.items?.find(i => i.colorId === selection.colorId)
                            || resolveFeaturedItem(product, alt.featuredColorId))
                        : resolveFeaturedItem(product, alt.featuredColorId);

                    if (!colorItem?.frontModelImage) return null;

                    const logos = colorItem.logos || [];

                    return (
                        <div key={idx} style={{ textAlign: 'center' }}>
                            {/* Label above the image */}
                            <div style={{
                                fontSize: '0.75em', fontWeight: isActive ? 700 : 400,
                                color: isActive ? '#0d6efd' : '#555', marginBottom: 4
                            }}>
                                {alt.label}
                            </div>

                            {/* Image card with logo overlays */}
                            <div style={{
                                display: 'block',
                                position: 'relative',
                                width: '100%',
                                border: isActive ? '2px solid #0d6efd' : '2px solid #dee2e6',
                                borderRadius: 6,
                                padding: 2,
                                boxShadow: isActive ? '0 0 0 3px rgba(13,110,253,0.2)' : 'none',
                                transition: 'box-shadow 0.15s ease, border-color 0.15s ease'
                            }}>
                                <img
                                    src={getImageUrl(colorItem.frontModelImage)}
                                    alt={colorItem.colorName}
                                    style={{ width: '100%', borderRadius: 4, display: 'block' }}
                                />
                                {logos.map((logo, i) => (
                                    <img
                                        key={i}
                                        src={apiUrl + logo.decoration_logo.imageUrl}
                                        alt={logo.decoration_logo.decorationName}
                                        style={{
                                            position: 'absolute',
                                            left: logo.x + '%',
                                            top: logo.y + '%',
                                            width: logo.width + '%',
                                            transform: 'rotate(' + logo.rotate + 'deg)'
                                        }}
                                    />
                                ))}
                            </div>
                            {/* Style code */}
                            {product.code && (
                                <div style={{ fontSize: '0.7em', color: '#888', marginBottom: 2 }}>
                                    {product.code}
                                </div>
                            )}
                            {/* Product title */}
                            {product.title && (
                                <div style={{ fontSize: '0.8em', fontWeight: 600, color: '#333', marginBottom: 2, maxWidth: '100%', wordWrap: 'break-word', lineHeight: 1.3 }}>
                                    {product.title}
                                </div>
                            )}

                            {/* Spec Sheet / Measurements links */}
                            {(colorItem.specSheet || colorItem.measurements) && (
                                <div style={{ fontSize: '0.75em', marginTop: 4 }}>
                                    {colorItem.specSheet && (
                                        <a
                                            href={colorItem.specSheet.startsWith('http') ? colorItem.specSheet : `https://www.apparelvideos.com/images/specsheet/pdf/specsheet/${colorItem.specSheet}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{ marginRight: 8 }}
                                        >
                                            Spec Sheet
                                        </a>
                                    )}
                                    {colorItem.measurements && (
                                        <a
                                            href={colorItem.measurements.startsWith('http') ? colorItem.measurements : `https://www.apparelvideos.com/images/specsheet/pdf/productmeasurements/${colorItem.measurements}`}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Measurements
                                        </a>
                                    )}
                                </div>
                            )}

                        </div>
                    );
                })}
            </div>
        );
    };

    const renderAddressField = (field) => {
        const selection = addressSelections[field.id] || {};
        const fieldError = errors[field.id];
        const showMissing = !!fieldError;

        if (field.addressType === 'select') {
            // Dropdown to select from predefined addresses
            return (
                <div style={{ border: fieldError ? "2px solid #dc3545" : "1px solid #CCCCCC", padding: 15, borderRadius: 5 }}>
                    <Form.Select
                        value={selection.addressId || ''}
                        onChange={(e) => handleAddressSelect(field.id, e.target.value)}
                        isInvalid={!!fieldError}
                    >
                        <option value="">-- Select an address --</option>
                        {addressData.map(addr => (
                            <option key={addr.id} value={addr.id}>
                                {addr.name ? `${addr.name}: ` : ''}{addr.street}, {addr.city}, {addr.state} {addr.zipCode}
                            </option>
                        ))}
                    </Form.Select>
                    {fieldError && (
                        <Alert variant="danger" className="mt-2 mb-0 py-2">
                            {fieldError}
                        </Alert>
                    )}
                    {selection.addressId && (
                        <Alert variant="info" className="mt-2 mb-0">
                            {(() => {
                                const addr = addressData.find(a => a.id === selection.addressId);
                                if (!addr) return null;
                                return (
                                    <>
                                        <strong>{addr.name || 'Selected Address'}</strong><br />
                                        {addr.street}<br />
                                        {addr.street2 && <>{addr.street2}<br /></>}
                                        {addr.city}, {addr.state} {addr.zipCode}
                                    </>
                                );
                            })()}
                        </Alert>
                    )}
                </div>
            );
        }

        // Manual entry form
        const zipInvalidFormat = !!selection.zipCode && !ZIP_CODE_REGEX.test(selection.zipCode);
        return (
            <div style={{ border: fieldError ? "2px solid #dc3545" : "1px solid #CCCCCC", padding: 15, borderRadius: 5 }}>
                <Row className="mb-2">
                    <Col>
                        <Form.Control
                            type="text"
                            placeholder="Street Address *"
                            value={selection.street || ''}
                            onChange={(e) => handleAddressChange(field.id, 'street', e.target.value)}
                            isInvalid={showMissing && !selection.street}
                        />
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col>
                        <Form.Control
                            type="text"
                            placeholder="Street Address 2 (optional)"
                            value={selection.street2 || ''}
                            onChange={(e) => handleAddressChange(field.id, 'street2', e.target.value)}
                        />
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col md={6}>
                        <Form.Control
                            type="text"
                            placeholder="City *"
                            value={selection.city || ''}
                            onChange={(e) => handleAddressChange(field.id, 'city', e.target.value)}
                            isInvalid={showMissing && !selection.city}
                        />
                    </Col>
                    <Col md={3}>
                        <Form.Select
                            value={selection.state || ''}
                            onChange={(e) => handleAddressChange(field.id, 'state', e.target.value)}
                            isInvalid={showMissing && !selection.state}
                        >
                            <option value="">State *</option>
                            {US_STATES.map(([abbr, name]) => (
                                <option key={abbr} value={abbr}>{name}</option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col md={3}>
                        <Form.Control
                            type="text"
                            placeholder="Zip Code *"
                            value={selection.zipCode || ''}
                            maxLength={5}
                            onChange={(e) => handleAddressChange(field.id, 'zipCode', e.target.value)}
                            isInvalid={zipInvalidFormat || (showMissing && !selection.zipCode)}
                        />
                        {zipInvalidFormat && (
                            <Form.Text className="text-danger">Zip code needs 5 digits</Form.Text>
                        )}
                    </Col>
                </Row>
                {fieldError && (
                    <Alert variant="danger" className="mt-2 mb-0 py-2">
                        {fieldError}
                    </Alert>
                )}
            </div>
        );
    };

    const renderField = (field) => {
        const fieldError = errors[field.id];

        switch (field.fieldType) {
            case 'string':
                return (
                    <Form.Control
                        type="text"
                        value={responses[field.id] || ''}
                        onChange={(e) => handleChange(field.id, field.mask ? applyMask(e.target.value, field.mask) : e.target.value)}
                        maxLength={field.mask ? field.mask.length : field.length}
                        placeholder={field.mask || ''}
                        isInvalid={!!fieldError}
                    />
                );
            case 'number':
                return (
                    <Form.Control
                        type="number"
                        value={responses[field.id] || ''}
                        onChange={(e) => handleChange(field.id, e.target.value)}
                        isInvalid={!!fieldError}
                    />
                );
            case 'date':
                return (
                    <Form.Control
                        type="date"
                        value={responses[field.id] || ''}
                        onChange={(e) => handleChange(field.id, e.target.value)}
                        isInvalid={!!fieldError}
                    />
                );
            case 'radio':
                const options = field.options ? JSON.parse(field.options) : [];
                const selectType = field.selectType || 'radio';

                if (selectType === 'select') {
                    // Dropdown (single selection)
                    return (
                        <Form.Select
                            value={responses[field.id] || ''}
                            onChange={(e) => handleChange(field.id, e.target.value)}
                            isInvalid={!!fieldError}
                        >
                            <option value="">-- Select an option --</option>
                            {options.map((option, idx) => (
                                <option key={idx} value={option}>{option}</option>
                            ))}
                        </Form.Select>
                    );
                } else if (selectType === 'multi') {
                    // Checkboxes (multiple selections)
                    const selectedValues = responses[field.id] ?
                        (typeof responses[field.id] === 'string' ?
                            (responses[field.id].startsWith('[') ? JSON.parse(responses[field.id]) : [responses[field.id]])
                            : responses[field.id])
                        : [];

                    const handleCheckboxChange = (option, checked) => {
                        let newValues;
                        if (checked) {
                            newValues = [...selectedValues, option];
                        } else {
                            newValues = selectedValues.filter(v => v !== option);
                        }
                        handleChange(field.id, JSON.stringify(newValues));
                    };

                    return (
                        <div>
                            {options.map((option, idx) => (
                                <Form.Check
                                    key={idx}
                                    type="checkbox"
                                    id={`field-${field.id}-option-${idx}`}
                                    label={option}
                                    checked={selectedValues.includes(option)}
                                    onChange={(e) => handleCheckboxChange(option, e.target.checked)}
                                    isInvalid={!!fieldError}
                                />
                            ))}
                        </div>
                    );
                } else {
                    // Radio buttons (single selection) - default
                    return (
                        <div>
                            {options.map((option, idx) => (
                                <Form.Check
                                    key={idx}
                                    type="radio"
                                    name={`field-${field.id}`}
                                    label={option}
                                    value={option}
                                    checked={responses[field.id] === option}
                                    onChange={(e) => handleChange(field.id, e.target.value)}
                                    isInvalid={!!fieldError}
                                />
                            ))}
                        </div>
                    );
                }
            case 'product':
                return renderProductField(field);
            case 'address':
                return renderAddressField(field);
            case 'image':
                // Image URL and caption are stored in the junction table (survey_survey_fields)
                // Image URL and caption are stored in the junction table (survey_survey_fields)
                const imageUrl = field.survey_survey_fields?.imageUrl;
                const caption = field.survey_survey_fields?.caption;
                // Construct full URL for uploaded images
                const fullImageUrl = imageUrl ? (imageUrl.startsWith('http') ? imageUrl : apiUrl + imageUrl) : null;
                return (
                    <div style={{ textAlign: 'center' }}>
                        {fullImageUrl && (
                            <img
                                src={fullImageUrl}
                                alt={field.title || 'Survey image'}
                                style={{
                                    maxWidth: '100%',
                                    height: 'auto',
                                    marginBottom: caption ? '10px' : '0',
                                    borderRadius: '4px'
                                }}
                            />
                        )}
                        {caption && (
                            <p style={{
                                fontStyle: 'italic',
                                color: '#666',
                                marginTop: '5px'
                            }}>
                                {caption}
                            </p>
                        )}
                    </div>
                );
            case 'gallery':
                const galleryImagesRaw = field.survey_survey_fields?.galleryImages;
                const parsedImages = galleryImagesRaw ? (typeof galleryImagesRaw === 'string' ? JSON.parse(galleryImagesRaw) : galleryImagesRaw) : [];
                // Handle both old format (array of URLs) and new format (array of {url, caption})
                const galleryImages = parsedImages.map(img => typeof img === 'string' ? { url: img, caption: '' } : img);
                const layout = field.survey_survey_fields?.galleryLayout || 'horizontal';

                const getGalleryStyle = () => {
                    switch (layout) {
                        case 'horizontal':
                            return { display: 'flex', flexDirection: 'row', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' };
                        case 'vertical':
                            return { display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' };
                        case 'grid':
                            return { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' };
                        default:
                            return { display: 'flex', flexDirection: 'row', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' };
                    }
                };

                const getImageStyle = () => {
                    switch (layout) {
                        case 'horizontal':
                            return { maxWidth: '300px', maxHeight: '250px', objectFit: 'cover', borderRadius: '4px' };
                        case 'vertical':
                            return { maxWidth: '100%', maxHeight: '400px', objectFit: 'contain', borderRadius: '4px' };
                        case 'grid':
                            return { width: '100%', height: '200px', objectFit: 'cover', borderRadius: '4px' };
                        default:
                            return { maxWidth: '300px', maxHeight: '250px', objectFit: 'cover', borderRadius: '4px' };
                    }
                };

                return (
                    <div>
                        <div style={getGalleryStyle()}>
                            {galleryImages.map((img, idx) => {
                                const imgUrl = img.url || img;
                                const fullUrl = imgUrl.startsWith('http') ? imgUrl : apiUrl + imgUrl;
                                return (
                                    <div key={idx} style={{ textAlign: 'center' }}>
                                        <img
                                            src={fullUrl}
                                            alt={img.caption || `Gallery image ${idx + 1}`}
                                            style={getImageStyle()}
                                        />
                                        {img.caption && (
                                            <p style={{
                                                fontStyle: 'italic',
                                                color: '#666',
                                                marginTop: '8px',
                                                fontSize: '0.9em'
                                            }}>
                                                {img.caption}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            case 'statictext':
                const staticHtml = field.survey_survey_fields?.staticText || '';
                return (
                    <div
                        className="survey-static-text"
                        dangerouslySetInnerHTML={{ __html: staticHtml }}
                    />
                );
            default:
                return null;
        }
    };

    if (loading) {
        return <Container className="mt-5" style={{ paddingBottom: 100 }}><p>Loading...</p></Container>;
    }

    if (error && !survey) {
        return (
            <Container className="mt-5 text-center" style={{ paddingBottom: 100 }}>
                <p>This survey is no longer accepting submissions.  Please contact the program administrator for any question.</p>
            </Container>
        );
    }

    if (survey && survey.closed) {
        return (
            <Container className="mt-5 text-center" style={{ paddingBottom: 100 }}>
                <h3 className="mb-3">{survey.title}</h3>
                <p>This survey is no longer accepting submissions.  Please contact the program administrator for any question.</p>
            </Container>
        );
    }

    if (submitted) {
        return (
            <Container className="mt-5 text-center" style={{ paddingBottom: 100 }}>
                <h3>Thank You!</h3>
                <p>Your response has been submitted successfully.</p>
            </Container>
        );
    }

    // Check survey availability window (compares current local date against startDate/endDate as YYYY-MM-DD).
    const todayStr = new Date().toISOString().slice(0, 10);
    if (survey.startDate && todayStr < survey.startDate) {
        return (
            <Container className="mt-5" style={{ paddingBottom: 100 }}>
                <h3 className="mb-2">{survey.title}</h3>
                {survey.description && <p className="text-muted mb-4">{survey.description}</p>}
                <Alert variant="info">
                    This survey is not yet open. It will open on <strong>{survey.startDate}</strong>.
                </Alert>
            </Container>
        );
    }
    if (survey.endDate && todayStr > survey.endDate) {
        return (
            <Container className="mt-5" style={{ paddingBottom: 100 }}>
                <h3 className="mb-2">{survey.title}</h3>
                {survey.description && <p className="text-muted mb-4">{survey.description}</p>}
                <Alert variant="secondary">
                    This survey closed on <strong>{survey.endDate}</strong> and is no longer accepting responses.
                </Alert>
            </Container>
        );
    }

    // Determine if there are any product fields with loaded data to show in the image column
    const productFields = survey.survey_fields?.filter(f => f.fieldType === 'product' && productData[f.id]) || [];
    const hasProductImages = productFields.length > 0;
    const imagesOnRight = survey.productImageSide !== 'left';

    const sortedFields = survey.survey_fields?.slice().sort((a, b) =>
        (a.survey_survey_fields?.displayOrder || 0) - (b.survey_survey_fields?.displayOrder || 0)
    ) || [];

    const renderFieldGroup = (field) => (
        <Form.Group key={field.id} className="mb-3">
            {field.fieldType === 'image' ? (
                <>
                    {field.title && (
                        <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>{field.title}</div>
                    )}
                    {renderField(field)}
                </>
            ) : field.fieldType === 'statictext' ? (
                renderField(field)
            ) : (
                <>
                    <Form.Label>
                        {field.title}
                        {field.required && <span className="text-danger"> *</span>}
                    </Form.Label>
                    {renderField(field)}
                    {errors[field.id] && (
                        <Form.Text className="text-danger">{errors[field.id]}</Form.Text>
                    )}
                </>
            )}
        </Form.Group>
    );

    return (
        <Container fluid className="mt-5" style={{ paddingBottom: 100 }}>
            {/* Title always at the very top on all screen sizes */}
            <h3 className="mb-2">{survey.title}</h3>
            {survey.description && (
                <p className="text-muted mb-4">{survey.description}</p>
            )}
            {error && <Alert variant="danger">{error}</Alert>}

            <Row>
                {/* Product images first in DOM so they stack above the form on mobile */}
                {hasProductImages && (
                    <Col
                        md={6}
                        className={`order-1 mb-4 mb-md-0 ${imagesOnRight ? 'order-md-2' : 'order-md-1'}`}
                        style={{
                            borderBottom: '1px solid #dee2e6',
                            paddingBottom: 24,
                            ...(imagesOnRight
                                ? {}
                                : { borderRight: '1px solid #dee2e6', paddingRight: 24 })
                        }}
                    >
                        <h5 className="mb-4" style={{ color: '#555' }}>Product Preview</h5>
                        {productFields.map(field => (
                            <div key={field.id}>
                                {field.title && (
                                    <div style={{ fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#888', marginBottom: 6 }}>
                                        {field.title}
                                    </div>
                                )}
                                {renderProductImagePanel(field)}
                            </div>
                        ))}
                    </Col>
                )}

                {/* Form fields */}
                <Col
                    md={hasProductImages ? 6 : 8}
                    className={`order-2${hasProductImages ? (imagesOnRight ? ' order-md-1' : ' order-md-2') : ' mx-auto'}`}
                    style={hasProductImages && imagesOnRight
                        ? { borderLeft: '1px solid #dee2e6', paddingLeft: 24 }
                        : {}}
                >
                    <Form onSubmit={handleSubmit}>
                        {sortedFields.map(field => renderFieldGroup(field))}
                        <Button variant="primary" type="submit" className="mt-3">
                            Submit
                        </Button>
                    </Form>
                </Col>
            </Row>
        </Container>
    );
};

export default PublicSurvey;

